import { Brand, Country, Language, SearchModes } from '@/domainLogic/index.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { Localization } from '../../../common/Localization';

export const bravoflyTourOperatorBPs = ['HOLIDAYSBRAVOFLYAT'];

// Product type
const isDP = (searchMode: SearchModes) => searchMode === SearchModes.DP;
const isHO = (searchMode: SearchModes) => searchMode === SearchModes.HO;

// Brand
const isLastminuteBrand = (brand: Brand) => brand === Brand.LASTMINUTE;
const isBravoFlyBrand = (brand: Brand) => brand === Brand.BRAVOFLY;
const isWegDeBrand = (brand: Brand) => brand === Brand.WEGDE;
const isBookingBrand = (brand: Brand) => brand === Brand.BOOKING;
const isHolidayPiratesBrand = (brand: Brand) => brand === Brand.HOLIDAYPIRATES;

// White label
const isWhiteLabel = (brand: Brand) => isWegDeBrand(brand) || isBookingBrand(brand) || isHolidayPiratesBrand(brand);
const isWegOrHolidayPirates = (brand: Brand) => isWegDeBrand(brand) || isHolidayPiratesBrand(brand);

// Country
const isCountryFR = (country: Country) => country === Country.FR;
const isCountryDE = (country: Country) => country === Country.DE;

// Tour Operator
export const isTODeal = (localization: Localization, isOpxDeal: boolean) => {
  const isOpxEnabled = localization?.configValuesApp?.opxEnabled;
  return isOpxEnabled && isOpxDeal;
};
const isNotTODeal = (localization: Localization, isOpxDeal: boolean) => {
  const isOpxEnabled = localization?.configValuesApp?.opxEnabled;
  return isOpxEnabled && !isOpxDeal;
};
const isBravoFlyTO = (localization: Localization) =>
  isBravoFlyBrand(localization.brand) && bravoflyTourOperatorBPs.includes(localization.businessProfile);

// Domain
const isFR = (localization: Localization) =>
  !isWegOrHolidayPirates(localization.brand) && isCountryFR(localization.country);
const isLM = (localization: Localization) =>
  isLastminuteBrand(localization.brand) &&
  !isWhiteLabel(localization.brand) &&
  !isCountryFR(localization.country) &&
  !isBravoFlyTO(localization);

// Tour operator badges
export const hasFlexMix = (searchInfoData: SearchInfoData, localization: Localization, isOpxDeal: boolean) =>
  isNotTODeal(localization, isOpxDeal) && isWegDeBrand(localization.brand) && isDP(searchInfoData.searchMode);

export const hasImmediateDiscount = (searchInfoData: SearchInfoData, localization: Localization, isOpxDeal: boolean) =>
  isNotTODeal(localization, isOpxDeal) && isWegDeBrand(localization.brand) && isHO(searchInfoData.searchMode);

export const hasCashback = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) && isWhiteLabel(localization.brand) && localization.lang === Language.DE;

export const hasWegDeLabel = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) &&
  isLM(localization) &&
  !isCountryDE(localization.country) &&
  !hasToLogo(localization, isOpxDeal) &&
  !isOpxUrlaub(localization, isOpxDeal);

export const hasLastminuteDeLabel = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) && isBravoFlyTO(localization);

export const hasLastminuteDeDeeplink = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) && isLastminuteBrand(localization.brand) && isCountryDE(localization.country);

export const hasFrenchOpxUsp = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) && isFR(localization);

export const hasToLogo = (localization: Localization, isOpxDeal: boolean) =>
  isTODeal(localization, isOpxDeal) && localization?.configValuesApp?.showToLogos;

export const isOpxUrlaub = (localization: Localization, isOpxDeal: boolean) =>
  localization?.configValuesApp?.opxUrlaubEnabled && isOpxDeal;
